import { useCallback, useEffect, useRef } from 'react';

import { useActivityBuilderContext } from './activity-builder.context';
import {
  ACTIVITY_BUILDER_A4_LONG_SIDE,
  SLIDE_HEIGHT_MINUS_HEADER_FOOTER,
  ACTIVITY_BUILDER_DRAWER_INLINE_BREAKPOINT,
} from './activity-builder.constants';
import * as Styled from './activity-builder-slide.style';

export const useSlideResize = () => {
  const ref = useRef<HTMLDivElement | null>(null);

  const { isZoomToFit, isDrawerActive, setSlideScaleRatio } =
    useActivityBuilderContext();

  const onResize = useCallback(() => {
    if (isZoomToFit && ref.current) {
      const containerWidth = ref.current.offsetWidth; // excludes padding
      const realWidth = containerWidth - Styled.SLIDE_CONTAINER_PADDING;
      const ratio = realWidth / ACTIVITY_BUILDER_A4_LONG_SIDE;
      const slide = ref.current.childNodes[0] as HTMLElement;
      slide.style.width = `${ACTIVITY_BUILDER_A4_LONG_SIDE}px`;
      slide.style.height = `${SLIDE_HEIGHT_MINUS_HEADER_FOOTER}px`;
      slide.style.transform = `scale(${ratio})`;
      slide.style.transformOrigin = 'left top';
      setSlideScaleRatio(ratio);
    }
  }, [isZoomToFit, setSlideScaleRatio]);

  // listen to window size changes
  useEffect(() => {
    window.addEventListener('resize', onResize, false);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  // listen to the zoomToFit setting changing
  useEffect(() => {
    if (!ref.current) return;
    if (isZoomToFit) {
      onResize();
    } else if (!isZoomToFit) {
      const slide = ref.current.childNodes[0] as HTMLElement;
      slide.style.width = '';
      slide.style.height = '';
      slide.style.transform = '';
      slide.style.transformOrigin = '';
    }
  }, [isZoomToFit, onResize]);

  // listen to the drawer opening/closing
  useEffect(() => {
    if (window.screen.availWidth > ACTIVITY_BUILDER_DRAWER_INLINE_BREAKPOINT) {
      onResize();
    }
  }, [isDrawerActive, onResize]);

  return {
    ref,
  };
};
